import React, { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { faDownload } from '@fortawesome/pro-solid-svg-icons'

import downloadNormativeIncompleteParticipantsReport from 'api/administration/courses/DownloadNormativeIncompleteParticipantsReport'
import { Modal } from 'components/reusables/modal'
import { ModalButtons } from 'components/reusables/modalButtons'
import { saveAs } from 'helpers'
import { formatThousandsSeparator } from 'helpers/others.helper'
import { useTranslationRankmi } from 'helpers/translations.helper'
import { useNotification } from 'hooks/notifications/useNotification'
import { useAccess } from 'contexts/AccessContext'

import { Body, BodyHighlight, BodyParagraph } from './styled'

interface NormativeIncompleteParticipantsModalProps {
  isOpen: boolean
  courseId: number
  incompleteCount: number
  onClose: () => void
}

const NormativeIncompleteParticipantsModal: React.FC<NormativeIncompleteParticipantsModalProps> =
  ({ isOpen, courseId, incompleteCount, onClose }) => {
    const { i18n } = useTranslation()
    const notification = useNotification()
    const { access } = useAccess()
    const canDownloadReport = access.canDownloadReportWithViewAccess()
    const [processing, setProcessing] = useState(false)
    const t = useTranslationRankmi({
      prefix:
        'administration.courses.list.normativeDataValidation.incompleteModal',
    })
    const tValidation = useTranslationRankmi({
      prefix: 'administration.courses.list.normativeDataValidation',
    })
    const formattedIncompleteCount = useMemo(
      () => formatThousandsSeparator(incompleteCount, i18n.language),
      [incompleteCount, i18n.language]
    )

    const handleDownloadReport = async () => {
      if (!courseId || processing) return

      setProcessing(true)
      try {
        const { blob, filename } =
          await downloadNormativeIncompleteParticipantsReport(courseId)
        onClose()
        saveAs(blob, filename)
      } catch {
        notification.error(tValidation('error'))
      } finally {
        setProcessing(false)
      }
    }

    return (
      <Modal
        isOpen={isOpen}
        title={t('title')}
        isConfirmation
        maxwidth={800}
        maxheight={400}
        close={onClose}
        actions={
          <ModalButtons
            secondaryText={t('close')}
            secondaryAction={onClose}
            disableSecondaryButton={processing}
            {...(canDownloadReport
              ? {
                  primaryText: t('downloadReport'),
                  primaryAction: handleDownloadReport,
                  disablePrimaryButton: processing || !courseId,
                  processing,
                  iconPrimary: faDownload,
                  iconPrimaryPosition: 'start' as const,
                }
              : {})}
          />
        }
      >
        <Body>
          <BodyParagraph>
            {t('bodyIntro')}
            <BodyHighlight>
              {formattedIncompleteCount} {t('bodyHighlightSuffix')}
            </BodyHighlight>
          </BodyParagraph>
          <BodyParagraph>
            <Trans
              i18nKey="administration.courses.list.normativeDataValidation.incompleteModal.bodyDetail"
              components={{
                boldFields: <strong />,
                boldMaster: <strong />,
              }}
            />
          </BodyParagraph>
        </Body>
      </Modal>
    )
  }

export default NormativeIncompleteParticipantsModal
